import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Slider,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import MicNoneIcon from "@mui/icons-material/MicNone";
import SentimentSatisfiedAltOutlinedIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined";
import { HOME_GLASS_BACKDROP } from "../../../../pages/Home/constants/homeGlass";
import { useAuth } from "../../../../app/context/AuthContext";
import ThemeBackdrop from "../../../../ui/components/ThemeBackdrop";
import {
  configPatchFromPreview,
  enabledOverlayOpacity,
  getPreviewSettings,
  getThemeConfigForFile,
  pruneThemeConfig,
  upsertThemeConfig,
} from "../../../../core/utils/themeConfig";
import {
  BG_FIT_OPTIONS,
  THEME_ACCENT,
  THEME_PANEL,
  DARK_BORDER,
  isImageFile,
} from "./shared";

const thumbnailStripSx = {
  display: "flex",
  gap: 1.2,
  overflowX: "auto",
  overflowY: "hidden",
  pb: 0.5,
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(255,255,255,0.2) transparent",
  "&::-webkit-scrollbar": { height: 6 },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,0.2)",
    borderRadius: 999,
  },
};

function ThemeImageDropZone({
  id,
  inputRef,
  dragActive,
  disabled,
  multiple = true,
  icon: Icon,
  title,
  subtitle,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  onBrowse,
  accentColor,
}) {
  return (
    <Box
      component="label"
      htmlFor={id}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        display: "block",
        borderRadius: 3,
        p: 2.5,
        minHeight: 148,
        width: "100%",
        boxSizing: "border-box",
        cursor: disabled ? "not-allowed" : "pointer",
        pointerEvents: disabled ? "none" : "auto",
        opacity: disabled ? 0.45 : 1,
        border: dragActive
          ? `1px dashed ${accentColor}`
          : "1px dashed rgba(255,255,255,0.14)",
        background: dragActive ? `${accentColor}12` : "rgba(255,255,255,0.02)",
        transition: "border-color 200ms ease, background 200ms ease",
        "&:hover": disabled
          ? {}
          : {
              borderColor: `${accentColor}88`,
              background: "rgba(255,255,255,0.04)",
            },
      }}
    >
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept="image/*"
        multiple={multiple}
        hidden
        disabled={disabled}
        onChange={onBrowse}
      />

      <Stack alignItems="center" spacing={1.1} textAlign="center">
        <Box        sx={{          width: 48,          height: 48,          borderRadius: "50%",          display: "grid",          placeItems: "center",          background: "rgba(255,255,255,0.06)",          border: "1px solid rgba(255,255,255,0.1)",        }}      >        <Icon sx={{ color: accentColor, fontSize: 24 }} />
      </Box>

        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{title}</Typography>
        <Typography sx={{ fontSize: 11, opacity: 0.45, lineHeight: 1.5, maxWidth: 260 }}>
          {subtitle}
        </Typography>
      <Button
        component="span"
        size="small"
        sx={{
            mt: 0.3,
          textTransform: "none",
          fontSize: 12,
          fontWeight: 600,
          borderRadius: 2,
          px: 1.8,
          py: 0.5,
          color: "rgba(255,255,255,0.9)",
          background: `${accentColor}33`,
          border: `1px solid ${accentColor}55`,
          "&:hover": { background: `${accentColor}44` },
        }}
      >
        Browse files
      </Button>
      </Stack>
    </Box>
  );
}

export default function ClientThemeStep({
  form,
  setForm,
  isEditMode = false,
  existingThemes = [],
  existingThemesLoading = false,
  deletingThemeNames = [],
  onRemoveExistingTheme,
  onRemoveAllExistingThemes,
}) {
  const themeFiles = form.theme;
  const themeConfig = form.themeConfig ?? [];
  const clientLabel = String(form.clientName ?? "").trim();

  const { userInfo } = useAuth();
  const bgInputRef = useRef(null);
  const previewUrlsRef = useRef(new Map());
  const [bgDragActive, setBgDragActive] = useState(false);
  const [activeSlideId, setActiveSlideId] = useState(null);
  const [activeExistingTheme, setActiveExistingTheme] = useState(null);
  const accentColor = THEME_ACCENT;
  const [backgroundFit, setBackgroundFit] = useState("fill");
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  const [overlayOpacity, setOverlayOpacity] = useState(40);

  const username = userInfo?.username || "admin";
  const userInitial = username.charAt(0).toUpperCase();
  const deletingSet = useMemo(
    () => new Set(deletingThemeNames.map(String)),
    [deletingThemeNames],
  );
  const isDeletingThemes = deletingThemeNames.length > 0;
  const showExistingGallery =
    isEditMode || existingThemesLoading || existingThemes.length > 0;

  const fileEntries = useMemo(
    () =>
      themeFiles.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}`,
        file,
      })),
    [themeFiles],
  );

  const previews = useMemo(
    () =>
      fileEntries.map((entry) => {
        let previewUrl = previewUrlsRef.current.get(entry.id);
        if (!previewUrl) {
          previewUrl = URL.createObjectURL(entry.file);
          previewUrlsRef.current.set(entry.id, previewUrl);
        }
        return { ...entry, previewUrl };
      }),
    [fileEntries],
  );

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    },
    [],
  );

  useEffect(() => {
    const activeIds = new Set(fileEntries.map((entry) => entry.id));
    previewUrlsRef.current.forEach((url, id) => {
      if (!activeIds.has(id)) {
        URL.revokeObjectURL(url);
        previewUrlsRef.current.delete(id);
      }
    });
  }, [fileEntries]);

  useEffect(() => {
    if (!previews.length) {
      setActiveSlideId(null);
      return;
    }
    if (activeExistingTheme && !activeSlideId) return;
    if (!activeSlideId || !previews.some((entry) => entry.id === activeSlideId)) {
      setActiveSlideId(previews[0].id);
    }
  }, [activeSlideId, activeExistingTheme, previews]);

  useEffect(() => {
    if (!existingThemes.length) {
      setActiveExistingTheme(null);
      return;
    }
    if (
      !activeExistingTheme ||
      !existingThemes.some((theme) => theme.name === activeExistingTheme)
    ) {
      if (activeSlideId) return;
      setActiveExistingTheme(existingThemes[0].name);
    }
  }, [activeExistingTheme, activeSlideId, existingThemes]);

  const activePreviewUrl = useMemo(() => {
    if (activeSlideId) {
    const activePending = previews.find((entry) => entry.id === activeSlideId);
    if (activePending?.previewUrl) return activePending.previewUrl;
    }

    const activeExisting = existingThemes.find(
      (theme) => theme.name === activeExistingTheme,
    );
    if (activeExisting?.url) return activeExisting.url;

    if (previews[0]?.previewUrl) return previews[0].previewUrl;

    return existingThemes.find((theme) => theme.url)?.url ?? "";
  }, [activeExistingTheme, activeSlideId, existingThemes, previews]);

  const activeFileName = useMemo(() => {
    if (activeSlideId) {
      return previews.find((entry) => entry.id === activeSlideId)?.file?.name ?? "";
    }
    return activeExistingTheme || "";
  }, [activeExistingTheme, activeSlideId, previews]);

  const previewSettingsPatch = useCallback(
    () =>
      configPatchFromPreview({
        backgroundFit,
        overlayEnabled,
        overlayOpacity,
      }),
    [backgroundFit, overlayEnabled, overlayOpacity],
  );

  const commitPreviewSettings = useCallback(
    (nextFit, nextEnabled, nextOpacity) => {
      if (!activeFileName) return;
      setForm((prev) => ({
        ...prev,
        themeConfig: upsertThemeConfig(
          prev.themeConfig,
          activeFileName,
          configPatchFromPreview({
            backgroundFit: nextFit,
            overlayEnabled: nextEnabled,
            overlayOpacity: nextOpacity,
          }),
        ),
      }));
    },
    [activeFileName, setForm],
  );

  useEffect(() => {
    if (!activeFileName) return;
    const settings = getPreviewSettings(
      getThemeConfigForFile(themeConfig, activeFileName),
    );
    setBackgroundFit(settings.backgroundFit);
    setOverlayEnabled(settings.overlayEnabled);
    setOverlayOpacity(settings.overlayOpacity);
  }, [activeFileName]);

  const remainingThemeNames = useCallback(
    (nextThemeFiles) => [
      ...existingThemes.map((theme) => theme.name),
      ...(nextThemeFiles ?? []).map((file) => file.name),
    ],
    [existingThemes],
  );

  const addFiles = useCallback(
    (fileList) => {
      if (!fileList?.length) return;

      const files = Array.from(fileList);
      const incoming = files.filter(isImageFile);
      if (!incoming.length) return;

      const patch = previewSettingsPatch();
      let firstNewId = null;

      setForm((prev) => {
      const existingKeys = new Set(
          prev.theme.map(
            (file) => `${file.name}-${file.size}-${file.lastModified}`,
          ),      );      const next = [...prev.theme];      let nextConfig = prev.themeConfig;

      incoming.forEach((file) => {
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        if (existingKeys.has(key)) return;
        existingKeys.add(key);        if (!firstNewId) firstNewId = key;
        next.push(file);
          nextConfig = upsertThemeConfig(nextConfig, file.name, patch);
      });

      if (next.length === prev.theme.length) return prev;
        return { ...prev, theme: next, themeConfig: nextConfig };
      });

        setActiveExistingTheme(null);
        if (firstNewId) setActiveSlideId(firstNewId);
    },
    [previewSettingsPatch, setForm],
  );

  const removeFile = useCallback(
    (id) => {
      const idx = fileEntries.findIndex((entry) => entry.id === id);
      if (idx < 0) return;
      setForm((prev) => {
        const nextTheme = prev.theme.filter((_, i) => i !== idx);
        return {
          ...prev,
          theme: nextTheme,
          themeConfig: pruneThemeConfig(
            prev.themeConfig,
            remainingThemeNames(nextTheme),
          ),
        };
      });
    },
    [fileEntries, remainingThemeNames, setForm],
  );

  const clearPendingFiles = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      theme: [],
      themeConfig: pruneThemeConfig(
        prev.themeConfig,
        existingThemes.map((theme) => theme.name),
      ),
    }));
    setActiveSlideId(null);
  }, [existingThemes, setForm]);

  const handleBgInputChange = (event) => {
    const { files } = event.target;
    if (files?.length) addFiles(files);
    event.target.value = "";
  };

  const makeDragHandlers = (setDragActive) => ({
    onDragEnter: (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setDragActive(true);
    },
    onDragOver: (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setDragActive(true);
    },
    onDragLeave: (event) => {
      if (event.currentTarget.contains(event.relatedTarget)) return;
      setDragActive(false);
    },
    onDrop: (event) => {
      event.preventDefault();
      setDragActive(false);
      return event.dataTransfer.files;
    },
  });

  const bgDrag = makeDragHandlers(setBgDragActive);

  const openFilePicker = () => bgInputRef.current?.click();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        {/* Left: Preview + fit / overlay controls */}
        <Box>
          {clientLabel ? (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: accentColor,
                  flexShrink: 0,
                }}
              />
              <Typography
                noWrap
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  color: "rgba(255,255,255,0.82)",
                }}
              >
                {clientLabel}
              </Typography>
            </Stack>
          ) : null}
              <Box
                  sx={{
              position: "relative",
              borderRadius: 3,
              overflow: "hidden",
              border: `1.5px solid ${accentColor}88`,
              boxShadow: `0 0 24px ${accentColor}22`,
              background: THEME_PANEL,
              aspectRatio: "16/10",
              maxHeight: 280,
              width: "100%",
            }}
          >
            <ThemeBackdrop
              url={activePreviewUrl}
              fit={backgroundFit}
              blur={6}
              overlay={overlayOpacity}
              overlayEnabled={overlayEnabled}
            />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            p: 2,
          }}
        >
          <Box
                sx={{
                  textAlign: "center",
                  my: 2.5,
                  mt: "auto",
                }}
              >
            <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    mb: 1.2,
                    opacity: 0.92,
                  }}
                >
              What&apos;s on your mind?
            </Typography>
            <Box
              sx={{
                borderRadius: 99,
                px: 1.5,
                py: 0.8,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                background: "rgba(255,255,255,0.1)",
                backdropFilter: HOME_GLASS_BACKDROP,
                border: "1px solid rgba(255,255,255,0.14)",
              }}
            >
              <Typography
                sx={{
                      flex: 1,
                      fontSize: 10.5,
                      opacity: 0.45,
                      textAlign: "left",
                      pl: 0.5,
                    }}
              >
                Type your message...
              </Typography>
              <AttachFileIcon sx={{ fontSize: 13, opacity: 0.45 }} />
              <MicNoneIcon sx={{ fontSize: 13, opacity: 0.45 }} />
              <SentimentSatisfiedAltOutlinedIcon sx={{ fontSize: 13, opacity: 0.45 }} />
            </Box>
          </Box>

          <Stack direction="row" alignItems="center" spacing={0.8} mt="auto">
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                fontSize: 10,
                fontWeight: 700,
                background: "rgba(255,255,255,0.15)",
              }}
            >
              {userInitial}
            </Box>
            <Typography sx={{ fontSize: 10.5, opacity: 0.65 }}>{username}</Typography>
          </Stack>
            </Box>
        </Box>

          <Box sx={{ mt: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              mb={1.2}
            >
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, opacity: 0.55 }}>
                Background Fit
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 11.5, opacity: 0.55 }}>Overlay</Typography>
                <Switch
                  size="small"
                  checked={overlayEnabled}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    const nextOpacity = enabled
                      ? enabledOverlayOpacity(overlayOpacity)
                      : overlayOpacity;
                    setOverlayEnabled(enabled);
                    setOverlayOpacity(nextOpacity);
                    commitPreviewSettings(backgroundFit, enabled, nextOpacity);
                  }}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: accentColor },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: accentColor,
                      opacity: 0.6,
                    },
                  }}
                />
              </Stack>
            </Stack>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={backgroundFit}
              onChange={(_, value) => {
                if (!value) return;
                setBackgroundFit(value);
                commitPreviewSettings(value, overlayEnabled, overlayOpacity);
              }}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                "& .MuiToggleButtonGroup-grouped": {
                  border: `1px solid ${DARK_BORDER}`,
                  borderRadius: "8px !important",
                  mx: 0,
                },
                "& .MuiToggleButton-root": {
                  flex: "1 1 auto",
                  textTransform: "none",
                  fontSize: 11,
                  fontWeight: 600,
                  py: 0.6,
                  color: "rgba(255,255,255,0.55)",
                  background: THEME_PANEL,
                  "&.Mui-selected": {
                    color: "#111",
                    background: accentColor,
                    "&:hover": { background: accentColor },
                  },
                },
              }}
            >
              {BG_FIT_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

              <Box
              sx={{
                mt: overlayEnabled ? 1.8 : 0,
                height: overlayEnabled ? "auto" : 0,
                overflow: "hidden",
                visibility: overlayEnabled ? "visible" : "hidden",
                pointerEvents: overlayEnabled ? "auto" : "none",
              }}
            >
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: 11.5, opacity: 0.55, fontWeight: 600 }}>
                    Overlay Opacity
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: accentColor, fontWeight: 600 }}>
                    {overlayOpacity}%
                  </Typography>
                </Stack>
                <Slider
                  size="small"
                  value={overlayOpacity}
                  min={0}
                  max={100}
                onChange={(_, value) => {
                  setOverlayOpacity(value);
                }}
                onChangeCommitted={(_, value) => {
                  const opacity = Array.isArray(value) ? value[0] : value;
                  setOverlayOpacity(opacity);
                  commitPreviewSettings(backgroundFit, overlayEnabled, opacity);
                }}
                  sx={{
                    color: accentColor,
                    "& .MuiSlider-rail": { opacity: 0.2 },
                    "& .MuiSlider-thumb": { width: 14, height: 14 },
                  }}
                />
              </Box>
        </Box>
      </Box>

        {/* Right: Upload + existing gallery + accent */}
          <Stack spacing={2}>
            <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1.2, opacity: 0.7 }}>
          Background Slides
        </Typography>
        <ThemeImageDropZone
          id="create-client-theme-input"
          inputRef={bgInputRef}
          dragActive={bgDragActive}
          disabled={false}
          multiple
          icon={CloudUploadOutlinedIcon}
          title="Drag & drop images here"
          subtitle="PNG, JPG or WebP · Select multiple files supported"
          accentColor={accentColor}
          onBrowse={handleBgInputChange}
          {...bgDrag}
          onDrop={(event) => {
            const files = bgDrag.onDrop(event);
            if (files?.length) addFiles(files);
          }}
        />
      </Box>

          {showExistingGallery ? (
        <Box>
          <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
                mb={1.2}
            >
            <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.7 }}>
                    Uploaded Backgrounds
              </Typography>
                {existingThemes.length > 0 && onRemoveAllExistingThemes ? (
                  <Button
                    size="small"
                    disabled={isDeletingThemes}
                    onClick={onRemoveAllExistingThemes}
                    sx={{
                      minWidth: 0,
                      textTransform: "none",
                      fontSize: 11.5,
                      color: "rgba(255,255,255,0.45)",
                      "&:hover": { color: "#ff8a80", background: "rgba(255,255,255,0.04)" },
                    }}
                  >
                    Delete all
            </Button>
                ) : null}
            </Stack>

            {existingThemesLoading ? (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={22} sx={{ color: accentColor }} />
              </Stack>
            ) : existingThemes.length > 0 ? (
              <Box sx={thumbnailStripSx}>
                {existingThemes.map((theme) => {
                  const isActive = theme.name === activeExistingTheme && !activeSlideId;
                  const isThemeDeleting = deletingSet.has(theme.name);

                  return (
                    <Box
                      key={theme.id || theme.name}
                      onClick={() => {
                        if (isThemeDeleting) return;
                        setActiveExistingTheme(theme.name);
                        setActiveSlideId(null);
                      }}
                      sx={{
                          position: "relative",
                          flex: "0 0 108px",
                          aspectRatio: "4/3",
                          borderRadius: 2,
                          overflow: "hidden",
                        cursor: isThemeDeleting ? "not-allowed" : "pointer",
                        opacity: isThemeDeleting ? 0.55 : 1,
                        border: isActive
                          ? `2px solid ${accentColor}`
                          : `1px solid ${DARK_BORDER}`,
                        boxShadow: isActive ? `0 0 12px ${accentColor}33` : "none",
                      }}
                    >
                      {theme.url ? (
                        <Box
                          component="img"
                          src={theme.url}
                          alt={theme.name}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "grid",
                            placeItems: "center",
                            background: THEME_PANEL,
                          }}
                        >
                          <Typography
                            sx={{
                                fontSize: 9,
                                opacity: 0.45,
                                px: 0.5,
                                textAlign: "center",
                              }}
                          >
                            No preview
                          </Typography>
                        </Box>
                      )}

                      {isThemeDeleting ? (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                            background: "rgba(0,0,0,0.45)"                          }}
                        >
                          <CircularProgress size={18} sx={{ color: accentColor }} />
                        </Box>
                      ) : onRemoveExistingTheme ? (
                        <IconButton
                          size="small"
                          aria-label={`Delete ${theme.name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onRemoveExistingTheme(theme.name);
                          }}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            width: 20,
                            height: 20,
                            p: 0,
                            bgcolor: "rgba(0,0,0,0.6)",
                            color: "rgba(255,255,255,0.9)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            zIndex: 2,
                            "&:hover": {
                              bgcolor: "rgba(220,38,38,0.9)",
                              color: "#fff",
                              borderColor: "rgba(255,255,255,0.35)",
                            },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      ) : null}
                      <Typography
                        title={theme.name}
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: 0,
                          px: 0.5,
                          py: 0.3,
                          fontSize: 8.5,
                          fontWeight: 500,
                          background: "linear-gradient(transparent, rgba(0,0,0,0.6))",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {theme.name}
                      </Typography>
                    </Box>
                  );
                })}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 11.5, opacity: 0.4 }}>
                  No backgrounds uploaded yet
                </Typography>
              )}
            </Box>
          ) : null}
        </Stack>
      </Box>

      {previews.length > 0 ? (
        <Box sx={{ mt: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, opacity: 0.65 }}>
              {previews.length} slide{previews.length === 1 ? "" : "s"} ready
            </Typography>
            <Button
              size="small"
              onClick={clearPendingFiles}
              sx={{
                minWidth: 0,
                textTransform: "none",
                fontSize: 11.5,
                color: "rgba(255,255,255,0.45)",
                "&:hover": { color: "#ff8a80", background: "rgba(255,255,255,0.04)" },
              }}
            >
              Clear all
            </Button>
          </Stack>

          <Box sx={thumbnailStripSx}>
            {previews.map((entry) => {
              const isActive = entry.id === activeSlideId;
              return (
                <Box
                  key={entry.id}
                  onClick={() => {
                        setActiveSlideId(entry.id);
                        setActiveExistingTheme(null);
                      }}
                  sx={{
                    position: "relative",
                    flex: "0 0 108px",
                    aspectRatio: "4/3",
                    borderRadius: 2,
                    overflow: "hidden",
                    cursor: "pointer",
                    border: isActive
                          ? `2px solid ${accentColor}`
                          : `1px solid ${DARK_BORDER}`,
                    boxShadow: isActive ? `0 0 12px ${accentColor}33` : "none",
                  }}
                >
                  <Box
                    component="img"
                    src={entry.previewUrl}
                    alt={entry.file.name}
                    sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      bgcolor: "#4ADE80",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 12, color: "#111" }} />
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={`Remove ${entry.file.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      removeFile(entry.id);
                    }}
                    sx={{
                      position: "absolute",
                      top: 4,
                      left: 4,
                      width: 20,
                      height: 20,
                      p: 0,
                      bgcolor: "rgba(0,0,0,0.6)",
                      color: "rgba(255,255,255,0.9)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      zIndex: 2,
                      "&:hover": {
                        bgcolor: "rgba(220,38,38,0.9)",
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.35)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                  <Typography
                    title={entry.file.name}
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      px: 0.5,
                      py: 0.3,
                      fontSize: 8.5,
                      fontWeight: 500,
                      background: "linear-gradient(transparent, rgba(0,0,0,0.6))",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {entry.file.name}
                  </Typography>
                </Box>
              );
            })}

            <Box
              role="button"
              tabIndex={0}
              onClick={openFilePicker}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                openFilePicker();
              }}
              sx={{
                flex: "0 0 108px",
                aspectRatio: "4/3",
                borderRadius: 2,
                border: "1px dashed rgba(255,255,255,0.16)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.4,
                cursor: "pointer",
                "&:hover": {
                  borderColor: `${accentColor}66`,
                  background: "rgba(255,255,255,0.03)",
                },
              }}
            >
              <AddIcon sx={{ fontSize: 20, opacity: 0.45 }} />
              <Typography sx={{ fontSize: 9.5, opacity: 0.45 }}>Add more</Typography>
          </Box>
        </Box>
          </Box>
        ) : null}
    </Box>
  );
}
